import type { MouseEvent as ReactMouseEvent, ReactNode } from "react";
import { Fragment, useRef, useState } from "react";
import { css, styled } from "styled-components";

import type { DropdownItemProps } from "common/components/dropdown/DropdownItem";
import { DropdownItem } from "common/components/dropdown/DropdownItem";
import { easing } from "common/styles";
import { useOutsideClickDetection } from "common/utilities";

type DropdownAlignment = "right" | "left";

const StyledContent = styled.div<{ alignment?: DropdownAlignment; isActive: boolean }>`
  position: absolute;
  overflow: hidden;
  border-radius: 5px;
  transform-origin: top left;
  margin-top: 5px;

  ${({ theme, isActive }) => css`
    background-color: ${theme.colors.backgroundSecondary};
    transform: scale(${isActive ? 1 : 0});
    opacity: ${isActive ? 1 : 0};
    transition:
      transform 0.5s ${easing.slightBounce},
      opacity 0.1s ease;
  `};

  ${({ alignment }) =>
    alignment === "right" &&
    css`
      right: 0;
      transform-origin: top right;
    `}
`;

const StyledDropdown = styled.div`
  position: relative;
`;

const StyledDropdownContentInner = styled.div<{ alignment: DropdownAlignment }>`
  background-color: ${({ theme }) => theme.colors.backgroundSecondary};
  border-radius: 5px;
  overflow: hidden;
  text-align: ${({ alignment }) => alignment};
`;

type IDropdownItem = { isDisabled?: boolean; key: string; label: ReactNode; onClick: (event: ReactMouseEvent) => void };
export type CustomDropdownItemComponentProps = DropdownItemProps & { handleDeactivate: () => void };
type CustomDropdownItemComponent = { component: (props: CustomDropdownItemComponentProps) => ReactNode; key: string };

type DropdownProps = {
  alignment?: DropdownAlignment;
  ButtonElement: (props: { isActive: boolean; onClick: (event: ReactMouseEvent) => void }) => ReactNode;
  className?: string;
  isProDropdown?: boolean;
  items: (IDropdownItem | CustomDropdownItemComponent)[];
};

const isItemCustomDropdownItemComponent = (
  item: IDropdownItem | CustomDropdownItemComponent,
): item is CustomDropdownItemComponent => "component" in item;

export const Dropdown = ({ alignment = "left", className, ButtonElement, items, isProDropdown }: DropdownProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isActive, setIsActive] = useState<boolean>(false);

  const handleDeactivate = () => setIsActive(false);

  useOutsideClickDetection(containerRef, handleDeactivate);

  const handleToggleButtonClick = (event: ReactMouseEvent) => {
    event.preventDefault();
    if (isActive) {
      handleDeactivate();
    } else {
      setIsActive(true);
    }
  };

  const onItemClickHandle = (item: IDropdownItem) => (event: ReactMouseEvent) => {
    if (item.onClick) {
      item.onClick(event);
    }
    handleDeactivate();
  };

  return (
    <StyledDropdown ref={containerRef} className={className} role="menu">
      <ButtonElement isActive={isActive} onClick={handleToggleButtonClick} />
      <StyledContent isActive={isActive} alignment={alignment}>
        <StyledDropdownContentInner alignment={alignment}>
          {items.map((item, index) => {
            if (isItemCustomDropdownItemComponent(item)) {
              return <Fragment key={item.key}>{item.component({ isActive, index, handleDeactivate })}</Fragment>;
            }
            return (
              <DropdownItem
                isActive={isActive}
                index={index}
                key={item.key}
                onClick={onItemClickHandle(item)}
                isDisabled={item.isDisabled}
                isProItem={isProDropdown}
              >
                {item.label}
              </DropdownItem>
            );
          })}
        </StyledDropdownContentInner>
      </StyledContent>
    </StyledDropdown>
  );
};
